/** Dispatched after a video analysis run is successfully submitted. */
export const ACTIVE_RUNS_REFRESH_EVENT = 'mentis:active-runs-refresh';

export type ActiveRunsRefreshDetail = {
  requestId?: string;
};

export function dispatchActiveRunsRefresh(detail?: ActiveRunsRefreshDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<ActiveRunsRefreshDetail>(ACTIVE_RUNS_REFRESH_EVENT, { detail })
  );
}

/** Backend exposes user-facing `running`; use internal_status when present. */
export function getRunLifecycleStatus(run: {
  status?: string;
  internal_status?: string;
}): string {
  return (run.internal_status || run.status || 'pending').toLowerCase();
}

export function isActiveLifecycleStatus(status: string): boolean {
  return !['completed', 'failed', 'cancelled'].includes(status);
}

/** Elapsed wall-clock time for active runs (API returns fractional seconds). */
export function formatElapsedDuration(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
    return '—';
  }
  const total = Math.floor(seconds);
  if (total < 60) {
    return `${total}s`;
  }
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Map backend progress (chunk phase is 10–70%) to a user-facing percent.
 * When all chunks are done but post-processing continues, show ≥72% and "Finalizing".
 */
export function getActiveRunDisplayProgress(run: {
  progress?: number;
  chunks_processed?: number;
  total_chunks?: number;
}): { percent: number; finalizing: boolean } {
  const apiPercent = Math.round((run.progress ?? 0) * 100);
  const total = run.total_chunks ?? 0;
  const done = run.chunks_processed ?? 0;

  if (total > 0 && done >= total) {
    return {
      percent: Math.max(apiPercent, 72),
      finalizing: apiPercent < 100,
    };
  }

  if (total > 0) {
    const chunkMapped = Math.round((0.1 + (done / total) * 0.6) * 100);
    return {
      percent: Math.max(apiPercent, chunkMapped),
      finalizing: false,
    };
  }

  return { percent: apiPercent, finalizing: false };
}

const RAW_BACKEND_MESSAGE =
  /submitting batch job|completed chunk|batch job complete|batch job .*\(poll|merging analysis results|batch_processing/i;

export type ActiveRunProgressContext = {
  message?: string;
  progress?: number;
  chunks_processed?: number;
  total_chunks?: number;
  status?: string;
  internal_status?: string;
  analysis_tier?: string;
  display_category_total?: number;
  llm_video_prep?: {
    optimized?: boolean;
    skip_reason?: string;
  };
};

const isFreeTierRun = (run: ActiveRunProgressContext): boolean =>
  (run.analysis_tier || '').toLowerCase() === 'free';

/** True while ffmpeg LLM prep is running (before chunk phase). */
export function isActiveRunPreprocessingPhase(run: ActiveRunProgressContext): boolean {
  const msg = (run.message || '').toLowerCase();
  if (msg.includes('optimizing video')) {
    return true;
  }
  const progress = run.progress ?? 0;
  const total = run.total_chunks ?? 0;
  const done = run.chunks_processed ?? 0;
  return progress > 0 && progress < 0.12 && total <= 0 && done <= 0;
}

/** Map chunk progress to creator insight areas (free) or schema categories (paid). */
export function getActiveRunCategoryProgress(run: ActiveRunProgressContext): {
  done: number;
  total: number;
  label: 'insight areas' | 'categories';
} | null {
  const categoryTotal = run.display_category_total ?? 0;
  const batchTotal = run.total_chunks ?? 0;
  const batchDone = run.chunks_processed ?? 0;
  if (categoryTotal <= 0 || batchTotal <= 0) return null;

  const done = Math.min(categoryTotal, Math.round((batchDone / batchTotal) * categoryTotal));
  return {
    done,
    total: categoryTotal,
    label: isFreeTierRun(run) ? 'insight areas' : 'categories',
  };
}

/** @deprecated Use getActiveRunCategoryProgress */
export function getActiveRunInsightProgress(run: ActiveRunProgressContext): {
  done: number;
  total: number;
} | null {
  const progress = getActiveRunCategoryProgress(run);
  if (!progress || !isFreeTierRun(run)) return null;
  return { done: progress.done, total: progress.total };
}

/** User-facing status line — hides raw chunk/batch diagnostics from the API. */
export function getActiveRunDisplayMessage(
  run: ActiveRunProgressContext,
  progress: { percent: number; finalizing: boolean }
): string {
  const status = getRunLifecycleStatus(run);
  const total = run.total_chunks ?? 0;
  const done = run.chunks_processed ?? 0;
  const msg = (run.message || '').trim();
  const free = isFreeTierRun(run);
  const categoryProgress = getActiveRunCategoryProgress(run);

  if (isActiveRunPreprocessingPhase(run)) {
    return free ? 'Preparing your video…' : 'Optimizing video for AI analysis…';
  }
  if (status === 'pending' || status === 'queued') {
    return 'Preparing your video…';
  }
  if (status === 'failed') {
    return 'Something went wrong — retry here or open History';
  }
  if (progress.finalizing) {
    return 'Finalizing your report…';
  }
  if (categoryProgress && categoryProgress.done > 0) {
    return `Analyzed ${categoryProgress.done} of ${categoryProgress.total} ${categoryProgress.label}`;
  }
  if (categoryProgress) {
    return free
      ? `Analyzing ${categoryProgress.total} insight areas…`
      : `Analyzing ${categoryProgress.total} categories…`;
  }
  if (total > 0 && done > 0) {
    return `Analyzed ${done} of ${total} categories`;
  }
  if (total > 0) {
    return `Analyzing ${total} categories…`;
  }
  if (msg && !RAW_BACKEND_MESSAGE.test(msg)) {
    return msg;
  }
  return free ? 'Analyzing creator insights…' : 'Analyzing your video…';
}
